// =============================================
// Supabase Client & Services
// Modern implementation with Realtime, Storage, and Full-Text Search
// =============================================

import { createClient, RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';

// Neon integration disabled for now
const useNeon = false; // !!import.meta.env.VITE_DATABASE_URL;

// Environment variables - HARDCODED for debugging
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://khiskkgackdtarqekvrs.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtoaXNra2dhY2tkdGFycWVrdnJzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njg0MjE0NTEsImV4cCI6MjA4Mzk5NzQ1MX0.qrj2CWHc4QtWuUkmV4ikx-i-KOfwdsB-NdroSCCZKZY';

// Check if Supabase is properly configured
export const isSupabaseConfigured = !!(supabaseUrl && supabaseAnonKey && supabaseUrl.includes('supabase'));

// Singleton: prevent "Multiple GoTrueClient instances" and AbortError when HMR re-runs this module
const GLOBAL_SUPABASE_KEY = '__CHEESECAKE_SUPABASE_CLIENT__';

function getSupabaseClient(): SupabaseClient {
    // Check if we already have a client in global scope
    if (typeof window !== 'undefined' && (window as any)[GLOBAL_SUPABASE_KEY]) {
        const existingClient = (window as any)[GLOBAL_SUPABASE_KEY];
        console.log('[Supabase] Reusing existing client');
        return existingClient;
    }

    console.log('[Supabase] Creating new client');
    
    const client = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true,
            storage: typeof window !== 'undefined' ? window.localStorage : undefined,
            storageKey: 'cheesecake-auth',
            flowType: 'pkce'
        },
        global: {
            headers: {
                'x-client-info': 'cheesecake-b2b@1.0.0'
            }
        },
        db: {
            schema: 'public'
        },
        realtime: {
            params: {
                eventsPerSecond: 2
            }
        }
    });

    // Store in global scope
    if (typeof window !== 'undefined') {
        (window as any)[GLOBAL_SUPABASE_KEY] = client;
    }

    return client;
}

export const supabase: SupabaseClient = getSupabaseClient();

// =============================================
// Type Definitions
// =============================================

export interface Database {
    public: {
        Tables: {
            profiles: {
                Row: {
                    id: string;
                    email: string;
                    first_name: string;
                    last_name: string;
                    company_name: string | null;
                    phone: string | null;
                    tax_number: string | null;
                    tax_office: string | null;
                    role: 'customer' | 'admin';
                    avatar_url: string | null;
                    total_spent: number;
                    discount_rate: number;
                    is_active: boolean;
                    created_at: string;
                    updated_at: string;
                };
                Insert: Omit<Database['public']['Tables']['profiles']['Row'], 'created_at' | 'updated_at' | 'total_spent'>;
                Update: Partial<Database['public']['Tables']['profiles']['Insert']>;
            };
            categories: {
                Row: {
                    id: string;
                    name: string;
                    slug: string;
                    description: string | null;
                    image_url: string | null;
                    icon: string | null;
                    sort_order: number;
                    is_active: boolean;
                    created_at: string;
                };
                Insert: Omit<Database['public']['Tables']['categories']['Row'], 'id' | 'created_at'>;
                Update: Partial<Database['public']['Tables']['categories']['Insert']>;
            };
            products: {
                Row: {
                    id: string;
                    category_id: string | null;
                    name: string;
                    slug: string;
                    description: string | null;
                    short_description: string | null;
                    images: string[];
                    price: number;
                    wholesale_price: number | null;
                    min_order_quantity: number;
                    stock: number;
                    low_stock_threshold: number;
                    unit: 'adet' | 'kg' | 'kutu';
                    weight: string | null;
                    ingredients: string[] | null;
                    allergens: string[] | null;
                    storage_conditions: string | null;
                    shelf_life: string | null;
                    features: string[];
                    is_active: boolean;
                    is_new: boolean;
                    is_featured: boolean;
                    rating: number;
                    review_count: number;
                    created_at: string;
                    updated_at: string;
                    category?: Database['public']['Tables']['categories']['Row'];
                };
                Insert: Omit<Database['public']['Tables']['products']['Row'], 'id' | 'created_at' | 'updated_at' | 'rating' | 'review_count' | 'category'>;
                Update: Partial<Database['public']['Tables']['products']['Insert']>;
            };
            orders: {
                Row: {
                    id: string;
                    user_id: string;
                    order_number: string;
                    status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'shipped' | 'delivered' | 'cancelled';
                    subtotal: number;
                    discount_amount: number;
                    shipping_cost: number;
                    total: number;
                    shipping_address: Record<string, any> | null;
                    billing_address: Record<string, any> | null;
                    payment_method: string | null;
                    payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
                    notes: string | null;
                    estimated_delivery: string | null;
                    delivered_at: string | null;
                    created_at: string;
                    updated_at: string;
                };
                Insert: Omit<Database['public']['Tables']['orders']['Row'], 'id' | 'order_number' | 'created_at' | 'updated_at'>;
                Update: Partial<Database['public']['Tables']['orders']['Insert']>;
            };
            order_items: {
                Row: {
                    id: string;
                    order_id: string;
                    product_id: string | null;
                    product_name: string;
                    product_image: string | null;
                    quantity: number;
                    unit_price: number;
                    total_price: number;
                    notes: string | null;
                };
                Insert: Omit<Database['public']['Tables']['order_items']['Row'], 'id'>;
                Update: Partial<Database['public']['Tables']['order_items']['Insert']>;
            };
            favorites: {
                Row: {
                    id: string;
                    user_id: string;
                    product_id: string;
                    created_at: string;
                };
                Insert: Omit<Database['public']['Tables']['favorites']['Row'], 'id' | 'created_at'>;
                Update: Partial<Database['public']['Tables']['favorites']['Insert']>;
            };
            notifications: {
                Row: {
                    id: string;
                    user_id: string;
                    title: string;
                    message: string;
                    type: 'info' | 'success' | 'warning' | 'error' | 'order';
                    is_read: boolean;
                    action_url: string | null;
                    created_at: string;
                };
                Insert: Omit<Database['public']['Tables']['notifications']['Row'], 'id' | 'created_at'>;
                Update: Partial<Database['public']['Tables']['notifications']['Insert']>;
            };
            reviews: {
                Row: {
                    id: string;
                    product_id: string;
                    user_id: string;
                    rating: number;
                    comment: string | null;
                    is_approved: boolean;
                    created_at: string;
                };
                Insert: Omit<Database['public']['Tables']['reviews']['Row'], 'id' | 'created_at' | 'is_approved'>;
                Update: Partial<Database['public']['Tables']['reviews']['Insert']>;
            };
            addresses: {
                Row: {
                    id: string;
                    user_id: string;
                    title: string;
                    full_address: string;
                    city: string;
                    district: string;
                    postal_code: string | null;
                    is_default: boolean;
                    created_at: string;
                };
                Insert: Omit<Database['public']['Tables']['addresses']['Row'], 'id' | 'created_at'>;
                Update: Partial<Database['public']['Tables']['addresses']['Insert']>;
            };
        };
    };
}

// =============================================
// Products Service (with Full-Text Search)
// =============================================

const supabaseProductsService = {
    async getAll(options?: {
        category?: string;
        search?: string;
        limit?: number;
        offset?: number;
        sortBy?: 'name' | 'price' | 'created_at' | 'rating';
        sortOrder?: 'asc' | 'desc';
        featured?: boolean;
        includeInactive?: boolean;
    }) {
        // ✅ OPTIMIZED: Sadece gerekli kolonlar
        let query = supabase
            .from('products')
            .select(`
                id, name, slug, description, short_description,
                images, price, wholesale_price, min_order_quantity,
                stock, low_stock_threshold, unit, weight,
                ingredients, allergens, storage_conditions, shelf_life,
                features, is_active, is_new, is_featured,
                created_at, updated_at, category_id,
                category:categories!category_id(id, name, slug, icon)
            `);

        // Only filter by is_active if includeInactive is not true
        if (!options?.includeInactive) {
            query = query.eq('is_active', true);
        }

        // Category filter
        if (options?.category && options.category !== 'all') {
            query = query.eq('category_id', options.category);
        }

        // Simple search with ilike (works without full-text search setup)
        if (options?.search) {
            query = query.ilike('name', `%${options.search}%`);
        }

        // Featured filter
        if (options?.featured) {
            query = query.eq('is_featured', true);
        }

        // Sorting
        const sortBy = options?.sortBy || 'created_at';
        const sortOrder = options?.sortOrder || 'desc';
        query = query.order(sortBy, { ascending: sortOrder === 'asc' });

        // Pagination
        if (options?.limit) {
            query = query.limit(options.limit);
        }
        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 20) - 1);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    },

    async getById(id: string) {
        // ✅ OPTIMIZED: Sadece gerekli kolonlar
        const { data, error } = await supabase
            .from('products')
            .select(`
                id, name, slug, description, short_description,
                images, price, wholesale_price, min_order_quantity,
                stock, low_stock_threshold, unit, weight,
                ingredients, allergens, storage_conditions, shelf_life,
                features, is_active, is_new, is_featured, created_at, updated_at,
                category:categories!category_id(id, name, slug, icon, description)
            `)
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    async getBySlug(slug: string) {
        // ✅ OPTIMIZED: Sadece gerekli kolonlar
        const { data, error } = await supabase
            .from('products')
            .select(`
                id, name, slug, description, short_description,
                images, price, wholesale_price, min_order_quantity,
                stock, low_stock_threshold, unit, weight,
                ingredients, allergens, storage_conditions, shelf_life,
                features, is_active, is_new, is_featured, created_at, updated_at,
                category:categories!category_id(id, name, slug, icon, description)
            `)
            .eq('slug', slug)
            .single();

        if (error) throw error;
        return data;
    },

    async getFeatured(limit = 6) {
        // ✅ OPTIMIZED: Sadece gerekli kolonlar
        const { data, error } = await supabase
            .from('products')
            .select(`
                id, name, slug, images, price, wholesale_price,
                min_order_quantity, stock, unit, is_active, is_featured,
                category:categories!category_id(id, name, slug, icon)
            `)
            .eq('is_active', true)
            .eq('is_featured', true)
            .limit(limit);

        if (error) throw error;
        return data || [];
    },

    async getNewArrivals(limit = 6) {
        // ✅ OPTIMIZED: Sadece gerekli kolonlar
        const { data, error } = await supabase
            .from('products')
            .select(`
                id, name, slug, images, price, wholesale_price,
                min_order_quantity, stock, unit, is_active, is_new, created_at,
                category:categories!category_id(id, name, slug, icon)
            `)
            .eq('is_active', true)
            .eq('is_new', true)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data || [];
    },

    async getLowStock(threshold = 20) {
        // ✅ OPTIMIZED: Sadece gerekli kolonlar, LIMIT 10
        const { data, error } = await supabase
            .from('products')
            .select('id, name, stock, unit, category:categories!category_id(name)')  // ✅ Sadece 5 alan
            .eq('is_active', true)
            .lt('stock', threshold)
            .order('stock', { ascending: true })
            .limit(10);  // ✅ Sadece ilk 10

        if (error) throw error;
        return data || [];
    },

    async create(product: Database['public']['Tables']['products']['Insert']) {
        const { data, error } = await supabase
            .from('products')
            .insert(product)
            .select(`*, categories(*)`)
            .single();

        return { data, error };
    },

    async update(id: string, updates: Database['public']['Tables']['products']['Update']) {
        const { data, error } = await supabase
            .from('products')
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq('id', id)
            .select(`*, categories(*)`)
            .single();

        return { data, error };
    },

    async delete(id: string) {
        const { error } = await supabase
            .from('products')
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    async updateStock(id: string, quantity: number, operation: 'add' | 'subtract' = 'subtract') {
        const { data: product } = await supabase
            .from('products')
            .select('stock')
            .eq('id', id)
            .single();

        if (!product) throw new Error('Product not found');

        const newStock = operation === 'add'
            ? product.stock + quantity
            : product.stock - quantity;

        const { error } = await supabase
            .from('products')
            .update({ stock: Math.max(0, newStock) })
            .eq('id', id);

        if (error) throw error;
    }
};

// =============================================
// Categories Service
// =============================================

const supabaseCategoriesService = {
    async getAll() {
        // ✅ OPTIMIZED: Sadece gerekli kolonlar
        const { data, error } = await supabase
            .from('categories')
            .select('id, name, slug, icon, description, sort_order')  // ✅ Sadece 6 kolon
            .eq('is_active', true)
            .order('sort_order', { ascending: true });

        if (error) throw error;
        return data || [];
    },

    async getById(id: string) {
        // ✅ OPTIMIZED: Sadece gerekli kolonlar
        const { data, error } = await supabase
            .from('categories')
            .select('id, name, slug, icon, description, sort_order')  // ✅ Sadece 6 kolon
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    async getWithProductCount() {
        const { data, error } = await supabase
            .from('categories')
            .select(`
                *,
                products:products(count)
            `)
            .eq('is_active', true)
            .order('sort_order', { ascending: true });

        if (error) throw error;
        return data || [];
    }
};

// =============================================
// Orders Service
// =============================================

const supabaseOrdersService = {
    async getByUserId(userId: string) {
        const { data, error } = await supabase
            .from('orders')
            .select(`
                *,
                items:order_items(*)
            `)
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    },

    async getById(orderId: string) {
        const { data, error } = await supabase
            .from('orders')
            .select(`
                *,
                items:order_items(*),
                user:profiles(first_name, last_name, email, phone, company_name)
            `)
            .eq('id', orderId)
            .single();

        if (error) throw error;
        return data;
    },

    async getAll(options?: { status?: string; limit?: number; offset?: number }) {
        let query = supabase
            .from('orders')
            .select(`
                *,
                items:order_items(*),
                user:profiles(first_name, last_name, email, company_name)
            `, { count: 'exact' })
            .order('created_at', { ascending: false });

        if (options?.status && options.status !== 'all') {
            query = query.eq('status', options.status);
        }

        if (options?.limit) {
            query = query.limit(options.limit);
        }

        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 20) - 1);
        }

        const { data, error, count } = await query;
        if (error) throw error;
        return { data: data || [], count };
    },

    async create(orderData: Database['public']['Tables']['orders']['Insert'], items: Database['public']['Tables']['order_items']['Insert'][]) {
        // Start transaction-like operation
        const { data: order, error: orderError } = await supabase
            .from('orders')
            .insert(orderData)
            .select()
            .single();

        if (orderError) throw orderError;

        // Insert order items
        const orderItems = items.map(item => ({
            ...item,
            order_id: order.id
        }));

        const { error: itemsError } = await supabase
            .from('order_items')
            .insert(orderItems);

        if (itemsError) throw itemsError;

        // Update product stocks
        for (const item of items) {
            if (item.product_id) {
                await productsService.updateStock(item.product_id, item.quantity, 'subtract');
            }
        }

        return order;
    },

    async updateStatus(orderId: string, status: string, note?: string) {
        const { data, error } = await supabase
            .from('orders')
            .update({
                status,
                updated_at: new Date().toISOString(),
                ...(status === 'delivered' ? { delivered_at: new Date().toISOString() } : {})
            })
            .eq('id', orderId)
            .select()
            .single();

        if (error) throw error;

        // Add to status history
        if (note) {
            await supabase
                .from('order_status_history')
                .insert({
                    order_id: orderId,
                    status,
                    note
                });
        }

        return data;
    }
};

// =============================================
// Favorites Service (with Optimistic UI support)
// =============================================

const favoritesService = {
    async getByUserId(userId: string) {
        const { data, error } = await supabase
            .from('favorites')
            .select(`
                *,
                product:products(*, categories(*))
            `)
            .eq('user_id', userId);

        if (error) throw error;
        return data || [];
    },

    async add(userId: string, productId: string) {
        const { data, error } = await supabase
            .from('favorites')
            .insert({ user_id: userId, product_id: productId })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async remove(userId: string, productId: string) {
        const { error } = await supabase
            .from('favorites')
            .delete()
            .match({ user_id: userId, product_id: productId });

        if (error) throw error;
    },

    async toggle(userId: string, productId: string) {
        const { data: existing } = await supabase
            .from('favorites')
            .select('id')
            .match({ user_id: userId, product_id: productId })
            .single();

        if (existing) {
            await this.remove(userId, productId);
            return { isFavorite: false };
        } else {
            await this.add(userId, productId);
            return { isFavorite: true };
        }
    },

    async isFavorite(userId: string, productId: string) {
        const { data } = await supabase
            .from('favorites')
            .select('id')
            .match({ user_id: userId, product_id: productId })
            .single();

        return !!data;
    }
};

// =============================================
// Profile Service
// =============================================

const supabaseProfileService = {
    async getProfile(userId: string) {
        const { data, error } = await supabase
            .from('profiles')
            .select(`
                *,
                addresses:addresses(*)
            `)
            .eq('id', userId)
            .single();

        if (error) throw error;
        return data;
    },

    async updateProfile(userId: string, updates: Database['public']['Tables']['profiles']['Update']) {
        const { data, error } = await supabase
            .from('profiles')
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq('id', userId)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async addAddress(userId: string, address: Omit<Database['public']['Tables']['addresses']['Row'], 'id' | 'user_id' | 'created_at'>) {
        // If setting as default, unset others first
        if (address.is_default) {
            await supabase
                .from('addresses')
                .update({ is_default: false })
                .eq('user_id', userId);
        }

        const { data, error } = await supabase
            .from('addresses')
            .insert({ ...address, user_id: userId })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async updateAddress(addressId: string, userId: string, updates: Partial<Database['public']['Tables']['addresses']['Row']>) {
        if (updates.is_default) {
            await supabase
                .from('addresses')
                .update({ is_default: false })
                .eq('user_id', userId);
        }

        const { data, error } = await supabase
            .from('addresses')
            .update(updates)
            .eq('id', addressId)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async deleteAddress(addressId: string) {
        const { error } = await supabase
            .from('addresses')
            .delete()
            .eq('id', addressId);

        if (error) throw error;
    }
};

// =============================================
// Notifications Service
// =============================================

const supabaseNotificationsService = {
    async getByUserId(userId: string, unreadOnly = false) {
        // ✅ OPTIMIZED: Sadece gerekli kolonlar, LIMIT 50
        let query = supabase
            .from('notifications')
            .select('id, title, message, type, is_read, action_url, created_at')  // ✅ Sadece 7 kolon
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(50);

        if (unreadOnly) {
            query = query.eq('is_read', false);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    },

    async markAsRead(notificationId: string) {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('id', notificationId);

        if (error) throw error;
    },

    async markAllAsRead(userId: string) {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('user_id', userId)
            .eq('is_read', false);

        if (error) throw error;
    },

    async create(notification: Database['public']['Tables']['notifications']['Insert']) {
        const { data, error } = await supabase
            .from('notifications')
            .insert(notification)
            .select()
            .single();

        if (error) throw error;
        return data;
    }
};

// =============================================
// Analytics Service (Admin Dashboard) - OPTIMIZED
// =============================================

const supabaseAnalyticsService = {
    async getDashboardStats() {
        try {
            const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

            console.log('[Analytics] Fetching optimized stats...');
            const startTime = performance.now();

            // ✅ OPTIMIZED: Paralel sorgular, sadece gerekli kolonlar
            const [revenueData, orderCount, customerCount, lowStockCount] = await Promise.all([
                // Query 1: Revenue - Sadece total kolonu
                supabase
                    .from('orders')
                    .select('total')  // ✅ Sadece 1 kolon
                    .gte('created_at', weekAgo)
                    .in('status', ['confirmed', 'preparing', 'ready', 'shipped', 'delivered'])
                    .then(({ data, error }) => {
                        if (error) throw error;
                        return data;
                    }),
                
                // Query 2: Order count - Sadece sayı
                supabase
                    .from('orders')
                    .select('id', { count: 'exact', head: true })  // ✅ head: true = veri çekme
                    .gte('created_at', weekAgo)
                    .then(({ count, error }) => {
                        if (error) throw error;
                        return count;
                    }),
                
                // Query 3: Customer count - Sadece sayı
                supabase
                    .from('profiles')
                    .select('id', { count: 'exact', head: true })  // ✅ head: true
                    .eq('role', 'customer')
                    .then(({ count, error }) => {
                        if (error) throw error;
                        return count;
                    }),
                
                // Query 4: Low stock count - Sadece sayı
                supabase
                    .from('products')
                    .select('id', { count: 'exact', head: true })  // ✅ head: true
                    .lt('stock', 20)
                    .then(({ count, error }) => {
                        if (error) throw error;
                        return count;
                    })
            ]);

            const duration = performance.now() - startTime;
            console.log(`[Analytics] ✅ Stats fetched in ${duration.toFixed(2)}ms`);

            const totalRevenue = revenueData?.reduce((sum: number, o: any) => sum + Number(o.total || 0), 0) || 0;

            const result = {
                totalRevenue,
                orderCount: orderCount || 0,
                customerCount: customerCount || 0,
                lowStockCount: lowStockCount || 0
            };

            // Cache result
            try {
                localStorage.setItem('dashboard_stats_cache', JSON.stringify({
                    data: result,
                    timestamp: Date.now()
                }));
            } catch (e) {
                console.warn('[Analytics] Cache failed:', e);
            }

            return result;
        } catch (error: any) {
            console.error('[Analytics] Error:', error);
            
            // Try cached data
            try {
                const cached = localStorage.getItem('dashboard_stats_cache');
                if (cached) {
                    const { data, timestamp } = JSON.parse(cached);
                    const age = Date.now() - timestamp;
                    if (age < 24 * 60 * 60 * 1000) {
                        console.log('[Analytics] Using cache');
                        return data;
                    }
                }
            } catch (e) {
                console.warn('[Analytics] Cache read failed:', e);
            }
            
            throw error;
        }
    },

    async getSalesChart(days = 7) {
        try {
            const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

            // ✅ OPTIMIZED: Sadece 3 kolon (created_at, total, status)
            const { data, error } = await supabase
                .from('orders')
                .select('created_at, total, status')  // ✅ Sadece gerekli kolonlar
                .gte('created_at', startDate)
                .in('status', ['confirmed', 'preparing', 'ready', 'shipped', 'delivered'])
                .order('created_at', { ascending: true });

            if (error) throw error;

            // Group by date (frontend'de, küçük veri seti)
            const grouped: Record<string, { total: number; count: number }> = {};
            
            (data || []).forEach((order: any) => {
                const date = order.created_at.split('T')[0];
                if (!grouped[date]) {
                    grouped[date] = { total: 0, count: 0 };
                }
                grouped[date].total += Number(order.total);
                grouped[date].count += 1;
            });

            return Object.entries(grouped)
                .map(([date, stats]) => ({
                    date,
                    revenue: stats.total,
                    orders: stats.count
                }))
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        } catch (error) {
            console.error('getSalesChart error:', error);
            return [];
        }
    },

    async getTopProducts(limit = 5) {
        try {
            // ✅ OPTIMIZED: Sadece 3 kolon, LIMIT 200
            const { data, error } = await supabase
                .from('order_items')
                .select('product_id, product_name, quantity')  // ✅ Tek satır
                .not('product_id', 'is', null)
                .limit(200);  // ✅ İlk 200 kayıt yeterli

            if (error) throw error;

            // Aggregate (frontend'de, küçük veri seti)
            const productMap: Record<string, { name: string; quantity: number }> = {};
            
            (data || []).forEach((item: any) => {
                if (!productMap[item.product_id]) {
                    productMap[item.product_id] = { name: item.product_name, quantity: 0 };
                }
                productMap[item.product_id].quantity += item.quantity;
            });

            return Object.entries(productMap)
                .map(([id, productData]) => ({ id, ...productData }))
                .sort((a, b) => b.quantity - a.quantity)
                .slice(0, limit);
        } catch (error) {
            console.error('getTopProducts error:', error);
            return [];
        }
    },

    async getCategoryDistribution() {
        try {
            // ✅ OPTIMIZED: LIMIT 200, sadece category name
            const { data, error } = await supabase
                .from('order_items')
                .select('product:products!inner(category:categories!inner(name))')  // ✅ inner join
                .limit(200);  // ✅ İlk 200 kayıt

            if (error) throw error;

            const categoryMap: Record<string, number> = {};
            
            (data || []).forEach((item: any) => {
                const categoryName = item.product?.category?.name || 'Diğer';
                categoryMap[categoryName] = (categoryMap[categoryName] || 0) + 1;
            });

            return Object.entries(categoryMap).map(([name, value]) => ({ name, value }));
        } catch (error) {
            console.error('getCategoryDistribution error:', error);
            return [];
        }
    }
};

// =============================================
// Storage Service (Image Upload)
// =============================================

const storageService = {
    async uploadProductImage(file: File, productId?: string): Promise<string> {
        const fileExt = file.name.split('.').pop();
        const fileName = `${productId || Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `products/${fileName}`;

        const { error } = await supabase.storage
            .from('products')
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false
            });

        if (error) throw error;

        // Get public URL
        const { data } = supabase.storage
            .from('products')
            .getPublicUrl(filePath);

        return data.publicUrl;
    },

    async uploadProductImages(files: File[], productId?: string): Promise<string[]> {
        const uploadPromises = files.map(file => this.uploadProductImage(file, productId));
        return Promise.all(uploadPromises);
    },

    async deleteProductImage(url: string): Promise<void> {
        try {
            // Extract path from URL
            const urlObj = new URL(url);
            const path = urlObj.pathname.split('/storage/v1/object/public/products/')[1];
            
            if (!path) {
                console.warn('Could not extract path from URL:', url);
                return;
            }

            const { error } = await supabase.storage
                .from('products')
                .remove([path]);

            if (error) throw error;
        } catch (error) {
            console.error('Error deleting image:', error);
            throw error;
        }
    },

    async uploadAvatar(file: File, userId: string): Promise<string> {
        const fileExt = file.name.split('.').pop();
        const filePath = `${userId}.${fileExt}`;

        const { error } = await supabase.storage
            .from('avatars')
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: true
            });

        if (error) throw error;

        const { data } = supabase.storage
            .from('avatars')
            .getPublicUrl(filePath);

        return data.publicUrl;
    },

    async deleteImage(bucket: string, path: string) {
        const { error } = await supabase.storage
            .from(bucket)
            .remove([path]);

        if (error) throw error;
    },

    getOptimizedUrl(url: string, options?: { width?: number; height?: number; quality?: number }) {
        if (!url || !url.includes('supabase')) return url;

        const params = new URLSearchParams();
        if (options?.width) params.set('width', options.width.toString());
        if (options?.height) params.set('height', options.height.toString());
        if (options?.quality) params.set('quality', options.quality.toString());

        return `${url}?${params.toString()}`;
    }
};

// =============================================
// Realtime Subscriptions
// =============================================

const realtimeService = {
    channels: new Map<string, RealtimeChannel>(),

    subscribeToOrders(userId: string, callback: (payload: any) => void): () => void {
        const channelName = `orders-${userId}`;

        if (this.channels.has(channelName)) {
            this.unsubscribe(channelName);
        }

        const channel = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'orders',
                    filter: `user_id=eq.${userId}`
                },
                callback
            )
            .subscribe();

        this.channels.set(channelName, channel);

        return () => this.unsubscribe(channelName);
    },

    subscribeToNotifications(userId: string, callback: (payload: any) => void): () => void {
        const channelName = `notifications-${userId}`;

        if (this.channels.has(channelName)) {
            this.unsubscribe(channelName);
        }

        const channel = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${userId}`
                },
                callback
            )
            .subscribe();

        this.channels.set(channelName, channel);

        return () => this.unsubscribe(channelName);
    },

    subscribeToProducts(callback: (payload: any) => void): () => void {
        const channelName = 'products-all';

        if (this.channels.has(channelName)) {
            this.unsubscribe(channelName);
        }

        const channel = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'products'
                },
                callback
            )
            .subscribe();

        this.channels.set(channelName, channel);

        return () => this.unsubscribe(channelName);
    },

    subscribeToAllOrders(callback: (payload: any) => void): () => void {
        const channelName = 'orders-admin';

        if (this.channels.has(channelName)) {
            this.unsubscribe(channelName);
        }

        const channel = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'orders'
                },
                callback
            )
            .subscribe();

        this.channels.set(channelName, channel);

        return () => this.unsubscribe(channelName);
    },

    unsubscribe(channelName: string) {
        const channel = this.channels.get(channelName);
        if (channel) {
            supabase.removeChannel(channel);
            this.channels.delete(channelName);
        }
    },

    unsubscribeAll() {
        this.channels.forEach((channel, _name) => {
            supabase.removeChannel(channel);
        });
        this.channels.clear();
    }
};

// =============================================
// Auth Helpers
// =============================================

const authHelpers = {
    async signUp(email: string, password: string, userData: { firstName: string; lastName: string; companyName: string; phone: string }) {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    first_name: userData.firstName,
                    last_name: userData.lastName,
                    company_name: userData.companyName,
                    phone: userData.phone
                }
            }
        });

        if (error) throw error;

        // Profile is automatically created via trigger or we insert it
        if (data.user) {
            await supabase.from('profiles').upsert({
                id: data.user.id,
                email,
                first_name: userData.firstName,
                last_name: userData.lastName,
                company_name: userData.companyName,
                phone: userData.phone,
                role: 'customer'
            });
        }

        return data;
    },

    async signIn(email: string, password: string) {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });

        if (error) throw error;
        return data;
    },

    async signOut() {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    },

    async getSession() {
        const { data: { session } } = await supabase.auth.getSession();
        return session;
    },

    async getCurrentUser() {
        const { data: { user } } = await supabase.auth.getUser();
        return user;
    },

    onAuthStateChange(callback: (event: string, session: any) => void) {
        return supabase.auth.onAuthStateChange(callback);
    }
};

// =============================================
// Export Services
// =============================================

// Database services - use Neon if configured, otherwise use Supabase
export const productsService = useNeon ? ({} as any) : supabaseProductsService;
export const categoriesService = useNeon ? ({} as any) : supabaseCategoriesService;
export const ordersService = useNeon ? ({} as any) : supabaseOrdersService;
export const analyticsService = useNeon ? ({} as any) : supabaseAnalyticsService;
export const profileService = useNeon ? ({} as any) : supabaseProfileService;
export const notificationsService = useNeon ? ({} as any) : supabaseNotificationsService;

// Always export Supabase-specific services
export { favoritesService, storageService, realtimeService, authHelpers };

export default supabase;



